import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ErrorBoundary from './ErrorBoundary';
import ReportScheduler from './ReportScheduler';
import RefreshButton from './RefreshButton';
import DateRange from './DateRange';
import moment from 'moment';
import ReportFilters from './ReportFilters';
import { Common } from '../../utils/common';
import CurrentDomains from './CurrentDomains';
import AppliedFilters from './AppliedFilters';
import FullScreenButton from '../dashboard/FullScreenButton';

function Layout({
  title,
  subtitle,
  controllerName,
  filters,
  fetchData,
  setFilters,
  defaultFilterState,
  selectInputs,
  children,
  datePickerMode = 'daterange',
  reportSchedulerTimeframeOptions,
}) {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false);
  const [controlledDateRange, setControlledDateRange] = useState({
    start: moment(filters.start_date, 'YYYYMMDD'),
    end: moment(filters.end_date, 'YYYYMMDD'),
  });

  let leftHeaderButton;
  if (Common.in_crm_mode()) {
    leftHeaderButton = null;
  } else if (controllerName) {
    leftHeaderButton = (
      <ReportScheduler
        controllerName={controllerName}
        filters={filters}
        timeframeOptions={reportSchedulerTimeframeOptions}
      />
    );
  } else {
    leftHeaderButton = (
      <FullScreenButton setIsFullScreen={setIsFullScreen} isFullScreen={isFullScreen} />
    );
  }

  return (
    <div className={`report-details-container ${isFullScreen ? 'panel-fullscreen-wrap' : ''}`}>
      <div className='report-details-header'>
        <div className='report-details-title'>
          <h1>{title}</h1>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
        <div className='panel-header'>
          <ErrorBoundary>
            {filters.domains ? (
              <>
                {leftHeaderButton}
                <RefreshButton dataCallbacks={fetchData} />
              </>
            ) : null}
            {sidebarIsOpen || datePickerMode === 'none' ? null : (
              <DateRange
                controlledDateRange={controlledDateRange}
                setControlledDateRange={setControlledDateRange}
                filters={filters}
                setFilters={setFilters}
                datePickerMode={datePickerMode}
              />
            )}
          </ErrorBoundary>
        </div>
      </div>
      {!Common.in_crm_mode() ||
      (Common.in_crm_mode() && Object.keys(defaultFilterState).length > 0) ? (
        <ErrorBoundary>
          <ReportFilters
            filters={filters}
            defaultFilterState={defaultFilterState}
            open={sidebarIsOpen}
            setOpen={setSidebarIsOpen}
            setFilters={setFilters}
            controlledDateRange={controlledDateRange}
            setControlledDateRange={setControlledDateRange}
            selectInputs={selectInputs}
            datePickerMode={datePickerMode}
          />
        </ErrorBoundary>
      ) : null}
      {filters.domains ? (
        <div>
          {!Common.in_crm_mode() ||
          (Common.in_crm_mode() && Object.keys(defaultFilterState).length > 0) ? (
            <div className='applied-filters-container'>
              <div style={{ marginBottom: '20px' }}>
                <button
                  className='btn btn-primary'
                  onClick={() => setSidebarIsOpen(!sidebarIsOpen)}
                >
                  <span className='fa fa-plus' /> Add Filters
                </button>
              </div>
              {Common.in_crm_mode() ? null : (
                <ErrorBoundary>
                  <CurrentDomains filters={filters} setFilters={setFilters} />
                </ErrorBoundary>
              )}
              <ErrorBoundary>
                <AppliedFilters
                  defaultFilterState={defaultFilterState}
                  filters={filters}
                  setFilters={setFilters}
                />
              </ErrorBoundary>
            </div>
          ) : null}
          <ErrorBoundary>{children}</ErrorBoundary>
        </div>
      ) : (
        <div style={{ marginTop: '50px' }} className='text-center'>
          <h4>No groups or locations selected</h4>
          <p>Get started running your report by selecting groups or locations</p>
          <div>
            <button className='btn btn-primary' onClick={() => setSidebarIsOpen(!sidebarIsOpen)}>
              <span className='fa fa-plus' /> Select groups and locations
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

Layout.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  controllerName: PropTypes.string,
  fetchData: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.func)]).isRequired,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  defaultFilterState: PropTypes.object.isRequired,
  selectInputs: PropTypes.object,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  datePickerMode: PropTypes.string,
  reportSchedulerTimeframeOptions: PropTypes.arrayOf(PropTypes.string),
};

export default Layout;
